import { multi, withDependencies } from '@wix/thunderbolt-ioc'
import type { IOnLinkClickHandler } from './types'
import type { BrowserWindow, Experiments, ILinkClickHandler } from '@wix/thunderbolt-symbols'
import {
	BrowserWindowSymbol,
	ExperimentsSymbol,
	NavigationClickHandlerSymbol,
	SiteLinkClickHandlerSymbol,
} from '@wix/thunderbolt-symbols'
import { yieldToMain, applyRetainedQueryParams } from '@wix/thunderbolt-commons'
import { isExperimentOpen } from '@wix/thunderbolt-experiments'

type HTMLElementTarget = HTMLElement | null
type PageClickHandlerRegistration = {
	handler: ILinkClickHandler
	pageId: string
}
const MASTER_PAGE_ID = 'masterPage'

const shouldAllowButtonTagTarget = (eTarget: HTMLElementTarget, experiments: Experiments) =>
	!isExperimentOpen(experiments, 'specs.thunderbolt.lightboxFromButton') ||
	(eTarget && eTarget.tagName.toLowerCase() !== 'button')

const getButtonOrAnchorTarget = (event: MouseEvent, experiments: Experiments) => {
	let eTarget = event.target as HTMLElementTarget

	while (
		eTarget &&
		(!eTarget.tagName || (eTarget.tagName.toLowerCase() !== 'a' && shouldAllowButtonTagTarget(eTarget, experiments)))
	) {
		eTarget = eTarget.parentNode as HTMLElementTarget
	}

	return eTarget
}

const SAME_PAGE_ANCHOR_FRAGMENTS = new Set(['#', '#SCROLL_TO_TOP', '#SCROLL_TO_BOTTOM'])

const shouldNotHandleLink = (anchorTarget: HTMLElementTarget) => {
	if (!anchorTarget) {
		return false
	}
	const href = anchorTarget.getAttribute('href') || ''
	const isSamePageAnchorFragment = SAME_PAGE_ANCHOR_FRAGMENTS.has(href)
	return anchorTarget.getAttribute('data-cancel-link') || (href.startsWith('#') && !isSamePageAnchorFragment)
}

export const onLinkClickHandler = (
	experiments: Experiments,
	navigationHandler: ILinkClickHandler,
	siteLinkClickHandlers: Array<ILinkClickHandler>,
	_browserWindow?: BrowserWindow
): IOnLinkClickHandler => {
	const pageClickHandlerRegistrations: Array<PageClickHandlerRegistration> = []
	const getPageClickHandlers = () =>
		[
			...pageClickHandlerRegistrations.filter(({ pageId }) => pageId === MASTER_PAGE_ID),
			...pageClickHandlerRegistrations.filter(({ pageId }) => pageId !== MASTER_PAGE_ID),
		].map(({ handler }) => handler)

	return {
		onLinkClick: async (e: MouseEvent) => {
			let shouldResumeDefault = true
			if (e.metaKey || e.ctrlKey) {
				return
			}
			const anchorTarget = getButtonOrAnchorTarget(e, experiments)

			if (!anchorTarget) {
				return
			}

			const href = anchorTarget.getAttribute('href') || ''
			if (shouldNotHandleLink(anchorTarget)) {
				return
			}

			const isEditable = !!anchorTarget.closest('[contenteditable="true"]')
			const shouldRunSynchronously =
				process.env.PACKAGE_NAME === 'thunderbolt-ds' || href.startsWith('blob:') || isEditable
			if (!shouldRunSynchronously) {
				e.preventDefault()
				await yieldToMain()
			}

			const fireHandlers = (handlers: Array<ILinkClickHandler>) => {
				for (const handler of handlers) {
					const didHandle = handler.handleClick(anchorTarget)
					if (didHandle) {
						shouldResumeDefault = false
						if (shouldRunSynchronously) {
							e.preventDefault()
						}
						e.stopPropagation()
						return
					}
				}
			}

			fireHandlers([...siteLinkClickHandlers, ...getPageClickHandlers(), navigationHandler])

			if (!shouldRunSynchronously && shouldResumeDefault) {
				if (window && href) {
					// apply retained query params for links existing the current tab
					const hrefWithInternalQueryParams = applyRetainedQueryParams(new URL(window.location.href), href).href
					window.open(hrefWithInternalQueryParams, anchorTarget.getAttribute('target') || '_self')
				}
			}
		},
		registerPageClickHandler: (handler: ILinkClickHandler, pageId: string) => {
			pageClickHandlerRegistrations.push({ handler, pageId })
		},
		removePageClickHandler: (handler: ILinkClickHandler, pageId: string) => {
			const index = pageClickHandlerRegistrations.findIndex(
				(currentHandler) => currentHandler.pageId === pageId && currentHandler.handler === handler
			)

			if (index !== -1) {
				pageClickHandlerRegistrations.splice(index, 1)
			}
		},
	}
}

export const OnLinkClickHandler = withDependencies(
	[ExperimentsSymbol, NavigationClickHandlerSymbol, multi(SiteLinkClickHandlerSymbol), BrowserWindowSymbol] as const,
	onLinkClickHandler
)
